import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { MatchTimer } from "../components/match/MatchTimer";
import { EventControls } from "../components/match/EventControls";
import { EventTimeline } from "../components/match/EventTimeline";
import { useMatchSocket } from "../hooks/useMatchSocket";
import { useMatchLogStore } from "../store/useMatchLogStore";
import { fetchAllMatchEvents } from "../lib/loggerApi";

export default function LiveMatch() {
  const { t } = useTranslation("matches");
  const { matchId } = useParams<{ matchId: string }>();

  // Use a default ID for testing if none provided
  const activeMatchId = matchId || "MATCH_DEMO_123";

  const {
    setCurrentMatch,
    operatorClock,
    operatorPeriod,
    isConnected,
    setLiveEvents,
    lastTimelineRefreshRequest,
  } = useMatchLogStore();

  const { sendEvent } = useMatchSocket({
    matchId: activeMatchId,
    enabled: true,
  });

  const hydrateEvents = useCallback(async () => {
    if (!activeMatchId) return;
    try {
      const events = await fetchAllMatchEvents(activeMatchId);
      // PMA-COCK-218/PMA-MATCH-031: pass the exact id this fetch was
      // issued with as sourceMatchId, so setLiveEvents can drop a stale
      // response that resolves after the operator has already navigated
      // to a different match (the id domain matches on both sides here --
      // see useMatchLogStore.ts's setLiveEvents comment for why the
      // per-event match_id filter this replaced was wrong instead).
      setLiveEvents(events, activeMatchId);
    } catch (err) {
      console.error("Failed to hydrate viewer events", err);
    }
  }, [activeMatchId, setLiveEvents]);

  // Initialize session
  useEffect(() => {
    setCurrentMatch(activeMatchId);
    void hydrateEvents();
  }, [activeMatchId, setCurrentMatch, hydrateEvents]);

  useEffect(() => {
    if (!lastTimelineRefreshRequest) return;
    void hydrateEvents();
  }, [lastTimelineRefreshRequest, hydrateEvents]);

  const handleEvent = (type: string, data: any = {}) => {
    // Capture current state from store (which is updated by MatchTimer)
    sendEvent({
      match_clock: operatorClock,
      period: operatorPeriod,
      team_id: "TEAM_A", // Hardcoded for demo/simplicity
      type: type,
      data: data,
    });
  };

  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="live-match-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t("liveMatch")}</h1>
          <p className="text-sm text-gray-500">Match ID: {activeMatchId}</p>
        </div>
        <div
          data-testid="live-match-connection-status"
          data-connection-state={isConnected ? "connected" : "disconnected"}
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
            isConnected
              ? "bg-green-100 text-green-800"
              : "bg-gray-100 text-gray-700"
          }`}
        >
          {isConnected ? "Connected" : "Offline / Connecting..."}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Controls */}
        <div className="lg:col-span-2 space-y-6">
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              Game Clock
            </h2>
            <MatchTimer />
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              Actions
            </h2>
            <EventControls onEvent={handleEvent} />
          </section>
        </div>

        {/* Right Column: Timeline */}
        <div
          className="lg:col-span-1"
          data-testid="live-match-timeline"
          aria-live="polite"
          aria-atomic="false"
        >
          <EventTimeline />
        </div>
      </div>
    </div>
  );
}
